import { Plus, Trash2 } from 'lucide-react'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { OfferModel } from '@/types'

import { CampaignProductPicker } from '../CampaignProductPicker'

interface Props {
  rules: Partial<OfferModel.VolumeDiscountRules>
  onChange: (rules: Partial<OfferModel.VolumeDiscountRules>) => void
}

type Tier = OfferModel.VolumeDiscountRules['tiers'][0]

export const VolumeDiscountEditor = ({ rules, onChange }: Props) => {
  const tiers = rules.tiers ?? []

  const addTier = () =>
    onChange({
      ...rules,
      tiers: [...tiers, { min_quantity: 1, discount_type: 'percentage', discount_value: 0 }],
    })

  const removeTier = (index: number) =>
    onChange({ ...rules, tiers: tiers.filter((_, i) => i !== index) })

  const updateTier = (index: number, patch: Partial<Tier>) =>
    onChange({ ...rules, tiers: tiers.map((t, i) => i === index ? { ...t, ...patch } : t) })

  return (
    <div className="space-y-4">
      <CampaignProductPicker
        label="Select product"
        selectedIds={rules.product_id ? [rules.product_id] : []}
        onChange={(ids) => onChange({ ...rules, product_id: ids[0] ?? '' })}
        multiple={false}
      />

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-ink">Discount tiers</p>
          <Button variant="secondary" onClick={addTier}>
            <Plus /> Add tier
          </Button>
        </div>

        {tiers.map((tier, i) => (
          <div key={i} className="grid grid-cols-1 gap-3 rounded-lg border border-border-subtle p-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Minimum quantity</Label>
              <Input
                type="number"
                min={1}
                value={tier.min_quantity}
                onChange={(e) => updateTier(i, { min_quantity: Number(e.target.value) })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Maximum quantity (blank for no limit)</Label>
              <Input
                type="number"
                min={1}
                value={tier.max_quantity ?? ''}
                placeholder="∞"
                onChange={(e) => updateTier(i, { max_quantity: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Discount type</Label>
              <Select
                value={tier.discount_type}
                onValueChange={(v) => updateTier(i, { discount_type: v as OfferModel.DiscountType })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="cash">Fixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Discount value</Label>
              <Input
                type="number"
                min={0}
                value={tier.discount_value}
                onChange={(e) => updateTier(i, { discount_value: Number(e.target.value) })}
              />
            </div>
            {tiers.length > 1 && (
              <div className="sm:col-span-2">
                <Button
                  variant="ghost"
                 
                  onClick={() => removeTier(i)}
                  className="text-error hover:text-error"
                >
                  <Trash2 /> Remove tier
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
